import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import SendDrinkModal from './SendDrinkModal';

interface IncomingGift {
  giftId: string;
  kind: 'toast' | 'drink';
  itemName?: string | null;
  from: string;
  anonymous?: boolean;
  message?: string | null;
}

interface Props {
  branchId: string;
  bookingId: string;
}

/**
 * While a guest is seated, listens for gifts sent to them and shows a face-saving prompt
 * (Accept / Cheers back / Maybe later). When a gift THEY sent is accepted, nudges "Send one back".
 * Also catches gifts that arrived while the app was closed (a one-shot fetch on mount).
 */
const DrinkGiftListener: React.FC<Props> = ({ branchId, bookingId }) => {
  const [queue, setQueue] = useState<IncomingGift[]>([]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // Reciprocity: when one of my gifts is accepted, offer to send one back to that table.
  const [sendBack, setSendBack] = useState<{ bookingId: string; label: string } | null>(null);
  const [openSendBack, setOpenSendBack] = useState(false);
  const seen = useRef<Set<string>>(new Set());

  const myUserId = React.useMemo(() => {
    const { accessToken } = apiService.getTokens();
    return accessToken ? getUserIdFromToken(accessToken) : null;
  }, []);

  const enqueue = useCallback((g: IncomingGift) => {
    if (!g?.giftId || seen.current.has(g.giftId)) return;
    seen.current.add(g.giftId);
    setQueue((q) => [...q, g]);
  }, []);

  // Catch anything pending that arrived while we were away.
  useEffect(() => {
    let alive = true;
    apiService.getMyGifts(bookingId).then((res) => {
      if (!alive) return;
      (res?.incoming || []).forEach((g: any) => enqueue({
        giftId: g.id, kind: g.kind, itemName: g.itemName, from: g.from || 'Someone in the venue', anonymous: g.anonymous, message: g.message,
      }));
    }).catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [bookingId, enqueue]);

  // Live: gifts to me, and acceptances of gifts I sent.
  useEffect(() => {
    if (!myUserId) return;
    const unsub = subscribeToChannel(
      `user:${myUserId}`,
      ['drink_gift:incoming', 'drink_gift:accepted'] as unknown as readonly string[],
      (event, data) => {
        if (event === 'drink_gift:incoming' && data?.giftId) {
          enqueue({ giftId: data.giftId, kind: data.kind, itemName: data.itemName, from: data.from || 'Someone in the venue', anonymous: data.anonymous, message: data.message });
        } else if (event === 'drink_gift:accepted' && data?.recipientBookingId) {
          setFlash(`🎉 ${data.recipientName || 'They'} raised a glass back!`);
          setSendBack({ bookingId: data.recipientBookingId, label: `${data.recipientName || 'Them'} · ${data.recipientTableLabel || 'their table'}` });
          window.setTimeout(() => setFlash(null), 6000);
        }
      }
    );
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [myUserId, enqueue]);

  const current = queue[0] || null;

  const respond = async (action: 'accept' | 'cheers_back' | 'maybe_later') => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await apiService.respondGift(current.giftId, action);
      if (action === 'accept' || action === 'cheers_back') {
        setFlash(current.kind === 'drink' ? '🍸 Enjoy your drink!' : '🍻 Cheers!');
        window.setTimeout(() => setFlash(null), 4000);
      }
    } catch { /* the prompt still clears; staff can help if needed */ }
    finally {
      setQueue((q) => q.slice(1));
      setBusy(false);
    }
  };

  return (
    <>
      {flash && (
        <div style={s.flash} onClick={() => sendBack && setOpenSendBack(true)}>
          <span>{flash}</span>
          {sendBack && <button style={s.flashBtn} onClick={(e) => { e.stopPropagation(); setOpenSendBack(true); }}>Send one back 🍻</button>}
        </div>
      )}

      {current && (
        <div style={s.overlay}>
          <div style={s.card}>
            <div style={{ fontSize: 42 }}>{current.kind === 'drink' ? '🍸' : '🍻'}</div>
            <div style={s.title}>
              {current.kind === 'drink' ? 'Someone sent you a drink!' : 'Someone raised a glass to you!'}
            </div>
            <div style={s.from}>from {current.from}</div>
            {current.kind === 'drink' && current.itemName && <div style={s.item}>{current.itemName}</div>}
            {current.message && <div style={s.msg}>“{current.message}”</div>}

            <button style={s.accept} disabled={busy} onClick={() => respond('accept')}>
              {current.kind === 'drink' ? 'Accept with thanks 🙏' : 'Accept 🙏'}
            </button>
            <button style={s.cheers} disabled={busy} onClick={() => respond('cheers_back')}>Cheers back 🍻</button>
            <button style={s.later} disabled={busy} onClick={() => respond('maybe_later')}>Maybe later</button>
          </div>
        </div>
      )}

      {openSendBack && sendBack && (
        <SendDrinkModal
          branchId={branchId}
          bookingId={bookingId}
          presetRecipientBookingId={sendBack.bookingId}
          presetRecipientLabel={sendBack.label}
          onClose={() => { setOpenSendBack(false); setSendBack(null); setFlash(null); }}
        />
      )}
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  flash: { position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, background: '#111', color: '#fff', padding: '10px 16px', borderRadius: 999, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, boxShadow: '0 6px 20px rgba(0,0,0,0.3)', maxWidth: '92vw' },
  flashBtn: { background: '#d97706', color: '#fff', border: 'none', borderRadius: 999, padding: '5px 12px', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 20 },
  card: { width: '100%', maxWidth: 360, background: 'var(--surface, #fff)', color: 'var(--text, #111)', borderRadius: 18, padding: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' },
  title: { fontSize: 19, fontWeight: 800 },
  from: { fontSize: 14, color: 'var(--text-3, #888)' },
  item: { fontSize: 15, fontWeight: 700, marginTop: 2 },
  msg: { fontStyle: 'italic', fontSize: 14, color: 'var(--text-2, #444)', marginTop: 2 },
  accept: { marginTop: 12, width: '100%', padding: 13, borderRadius: 12, border: 'none', background: '#d97706', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer' },
  cheers: { width: '100%', padding: 13, borderRadius: 12, border: '1px solid #d97706', background: 'transparent', color: '#d97706', fontWeight: 800, fontSize: 15, cursor: 'pointer' },
  later: { width: '100%', padding: 11, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--text-3, #888)', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
};

export default DrinkGiftListener;
